import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "./firebase";
import {
  doc, getDoc, collection, query, where, getDocs,
  addDoc, setDoc, updateDoc, serverTimestamp,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuth } from "./App";
import { NFL_TEAMS, teamLogoUrl, getCurrentNFLWeek } from "./nfl-teams";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function kickoffPassed(game) {
  if (!game?.kickoff) return false;
  const ts = game.kickoff.toDate ? game.kickoff.toDate() : new Date(game.kickoff);
  return new Date() >= ts;
}

function formatKickoff(game) {
  if (!game?.kickoff) return "TBD";
  const ts = game.kickoff.toDate ? game.kickoff.toDate() : new Date(game.kickoff);
  return ts.toLocaleString("en-US", {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit", timeZoneName: "short",
  });
}

function TeamLogo({ name, size = 36 }) {
  const team = NFL_TEAMS.find((t) => t.name === name);
  if (!team) return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "rgba(255,255,255,0.4)", flexShrink: 0 }}>
      {name?.substring(0, 3)}
    </div>
  );
  return (
    <img src={teamLogoUrl(team.espnId)} alt={name} width={size} height={size}
      style={{ borderRadius: "50%", objectFit: "contain", background: "white", padding: 2, flexShrink: 0 }}
      onError={(e) => { e.target.style.display = "none"; }}
    />
  );
}

// ─── Entry Selector ───────────────────────────────────────────────────────────

function EntrySelector({ entries, selectedId, onSelect }) {
  if (entries.length <= 1) return null;
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
      {entries.map((e) => (
        <button key={e.id} onClick={() => onSelect(e.id)} style={{
          padding: "6px 14px", borderRadius: 8, border: "1px solid",
          borderColor: selectedId === e.id ? "#FF6B35" : "rgba(255,255,255,0.15)",
          background: selectedId === e.id ? "rgba(255,107,53,0.18)" : "transparent",
          color: selectedId === e.id ? "#FF6B35" : "rgba(255,255,255,0.5)",
          fontWeight: 700, fontSize: 13, cursor: "pointer",
        }}>
          {e.name}
        </button>
      ))}
    </div>
  );
}

// ─── Dog-Flip Banner ──────────────────────────────────────────────────────────

function FlipBanner({ pick, games }) {
  const game = games.find((g) => g.id === pick?.gameId);
  if (!pick || pick.status !== "flipped" || !game) return null;
  return (
    <div style={{
      background: "rgba(229,57,53,0.12)", border: "1px solid rgba(229,57,53,0.4)",
      borderRadius: 12, padding: "14px 18px", marginBottom: 16,
      display: "flex", alignItems: "flex-start", gap: 12,
    }}>
      <div style={{ fontSize: 22, flexShrink: 0 }}>⚠️</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, color: "#ff6b6b", marginBottom: 4 }}>
          Your pick ({pick.teamSelected}) is no longer an underdog!
        </div>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", lineHeight: 1.5 }}>
          The line moved and {pick.teamSelected} is now favored. Pick a new underdog before kickoff
          or you'll be auto-assigned the Monday Night underdog.
        </div>
      </div>
    </div>
  );
}

// ─── Game Pick Card ───────────────────────────────────────────────────────────

function GameCard({ game, pick, onPick, locked }) {
  const isPicked = pick?.gameId === game.id;
  const isKickoffPast = kickoffPassed(game);
  const isFinal = game.status === "final";
  // Cards use flex column with fixed sections to ensure uniform height across the grid

  const underdogWon = isFinal && game.underdogTeam && (
    (game.underdogTeam === game.homeTeam && game.homeScore > game.awayScore) ||
    (game.underdogTeam === game.awayTeam && game.awayScore > game.homeScore)
  );

  return (
    <div style={{
      background: "var(--navy-card)",
      border: `1px solid ${isPicked && !isFinal ? "rgba(255,107,53,0.5)" : "var(--border)"}`,
      borderRadius: 14, padding: 16, transition: "all 0.15s",
      boxShadow: isPicked && !isFinal ? "0 0 16px rgba(255,107,53,0.15)" : undefined,
    }}>
      {/* Time */}
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginBottom: 10, display: "flex", justifyContent: "space-between" }}>
        <span>{formatKickoff(game)}</span>
        {isFinal && <span style={{ color: "#00C853", fontWeight: 700 }}>FINAL</span>}
        {!isFinal && isKickoffPast && <span style={{ color: "#FF6B35", fontWeight: 700 }}>IN PROGRESS</span>}
        {game.isMNF && !isFinal && <span style={{ color: "#FFD700", fontWeight: 700, fontSize: 10 }}>MNF</span>}
        {game.isTNF && !isFinal && <span style={{ color: "#00C853", fontWeight: 700, fontSize: 10 }}>TNF</span>}
      </div>

      {/* Teams — underdog row is the pick button */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
        {[game.awayTeam, game.homeTeam].map((team, teamIdx) => {
          const isHome = teamIdx === 1;
          const isUnderdog = team === game.underdogTeam;
          const score = isHome ? game.homeScore : game.awayScore;
          const oppScore = isHome ? game.awayScore : game.homeScore;
          const isWinner = isFinal && score > oppScore;
          const showSpread = isUnderdog && !isFinal && game.spread;
          const showClose = isUnderdog && isFinal && game.closingSpread;
          const isPickable = isUnderdog && !isFinal && !isKickoffPast && !locked;
          const isSelected = isPicked && isUnderdog;

          return (
            <div
              key={team}
              onClick={() => isPickable && onPick(game)}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                height: 40, borderRadius: 8, padding: "0 8px",
                background: isSelected
                  ? "rgba(0,200,83,0.12)"
                  : isUnderdog && !isFinal
                    ? "rgba(255,107,53,0.07)"
                    : "transparent",
                border: isSelected
                  ? "1px solid rgba(0,200,83,0.5)"
                  : isUnderdog && !isFinal
                    ? "1px solid rgba(255,107,53,0.2)"
                    : "1px solid transparent",
                cursor: isPickable ? "pointer" : "default",
                transition: "all 0.15s",
              }}
            >
              <TeamLogo name={team} size={26} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{
                    fontSize: 13,
                    fontWeight: isUnderdog ? 700 : 400,
                    color: isWinner ? "white" : isFinal && !isWinner ? "rgba(255,255,255,0.35)" : isUnderdog ? "white" : "rgba(255,255,255,0.7)",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>
                    {team}
                  </span>
                  <span style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: 0.5,
                    color: isHome ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.2)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 3, padding: "1px 4px", flexShrink: 0,
                  }}>
                    {isHome ? "HOME" : "AWAY"}
                  </span>
                </div>
              </div>

              {/* Right side: score / spread / pick hint */}
              {isFinal && score !== null && score !== undefined ? (
                <span style={{ fontSize: 16, fontWeight: 700, color: isWinner ? "white" : "rgba(255,255,255,0.3)", flexShrink: 0 }}>{score}</span>
              ) : showSpread ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  <span style={{ fontSize: 15, fontWeight: 800, color: isSelected ? "#00C853" : "#FF6B35" }}>
                    +{game.spread}
                  </span>
                  {isPickable && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: isSelected ? "#00C853" : "rgba(255,107,53,0.5)", letterSpacing: 0.5 }}>
                      {isSelected ? "✓ PICKED" : "TAP"}
                    </span>
                  )}
                  {isSelected && isKickoffPast && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#FF6B35" }}>✓</span>
                  )}
                </div>
              ) : showClose ? (
                <div style={{ textAlign: "center", flexShrink: 0 }}>
                  <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", letterSpacing: 0.5 }}>CLOSE</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#FFD700" }}>+{game.closingSpread}</div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Result or Pick button */}
      {isFinal ? (
        <div style={{
          textAlign: "center", padding: "8px", borderRadius: 8,
          background: isPicked ? (underdogWon ? "rgba(0,200,83,0.15)" : "rgba(229,57,53,0.12)") : "rgba(255,255,255,0.04)",
          border: `1px solid ${isPicked ? (underdogWon ? "rgba(0,200,83,0.3)" : "rgba(229,57,53,0.3)") : "var(--border)"}`,
        }}>
          {isPicked ? (
            <span style={{ fontSize: 13, fontWeight: 700, color: underdogWon ? "#00C853" : "#E53935" }}>
              {underdogWon ? `✅ +${pick.pointsEarned || game.closingSpread || game.spread} pts — ${game.underdogTeam} upset!` : `❌ ${game.underdogTeam} lost`}
            </span>
          ) : (
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.25)" }}>No pick made</span>
          )}
        </div>
      ) : game.underdogTeam ? (
        isKickoffPast ? (
          <div style={{ textAlign: "center", fontSize: 12, color: "rgba(255,255,255,0.25)", padding: "6px 0" }}>
            🔒 Locked
          </div>
        ) : null
      ) : (
        <div style={{ textAlign: "center", fontSize: 12, color: "rgba(255,255,255,0.25)", padding: "8px 0" }}>
          Line not yet available
        </div>
      )}
    </div>
  );
}

// ─── Week Picks Tab ───────────────────────────────────────────────────────────

function WeekPicksTab({ contest, myEntries, initialEntryId }) {
  const { user } = useAuth();
  const [week, setWeek] = useState(getCurrentNFLWeek() || 1);
  const [games, setGames] = useState([]);
  const [picks, setPicks] = useState([]);
  const [selectedEntryId, setSelectedEntryId] = useState(initialEntryId || myEntries[0]?.id || null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const currentPick = picks.find((p) => p.entryId === selectedEntryId && p.week === week);
  const allWeeks = Array.from({ length: 18 }, (_, i) => i + 1);

  // Update selected entry if initialEntryId changes (from entries tab click)
  useEffect(() => {
    if (initialEntryId) setSelectedEntryId(initialEntryId);
  }, [initialEntryId]);

  useEffect(() => {
    if (!selectedEntryId) return;
    loadWeekData();
  }, [week, selectedEntryId, contest.id]);

  const loadWeekData = async () => {
    setLoading(true);
    try {
      const gamesQ = query(
        collection(db, "ue_games"),
        where("contestId", "==", contest.id),
        where("week", "==", week)
      );
      const gSnap = await getDocs(gamesQ);
      const gamesData = gSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setGames(gamesData.sort((a, b) => {
        const ta = a.kickoff?.toDate ? a.kickoff.toDate() : new Date(a.kickoff || 0);
        const tb = b.kickoff?.toDate ? b.kickoff.toDate() : new Date(b.kickoff || 0);
        return ta - tb;
      }));

      const picksQ = query(
        collection(db, "ue_picks"),
        where("entryId", "==", selectedEntryId),
        where("week", "==", week)
      );
      const pSnap = await getDocs(picksQ);
      setPicks(pSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error("Error loading week data:", e); }
    setLoading(false);
  };

  const makePick = async (game) => {
    if (!selectedEntryId || saving) return;
    if (kickoffPassed(game)) return;
    setSaving(true);
    try {
      const pickData = {
        contestId: contest.id, entryId: selectedEntryId,
        ownerUid: user.uid, week,
        gameId: game.id, teamSelected: game.underdogTeam,
        spread: game.spread, status: "pending", pointsEarned: 0,
        pickedAt: serverTimestamp(),
      };
      if (currentPick?.id) {
        await setDoc(doc(db, "ue_picks", currentPick.id), pickData, { merge: true });
        setPicks((prev) => prev.map((p) => p.id === currentPick.id ? { ...p, ...pickData, id: currentPick.id } : p));
      } else {
        const ref = await addDoc(collection(db, "ue_picks"), pickData);
        setPicks((prev) => [...prev, { id: ref.id, ...pickData }]);
      }
    } catch (e) { console.error("Error making pick:", e); }
    setSaving(false);
  };

  const weekLocked = games.length > 0 && games.every((g) => kickoffPassed(g));

  if (!selectedEntryId) {
    return <div style={{ textAlign: "center", padding: 40, color: "rgba(255,255,255,0.4)" }}>No entries found. Add an entry first.</div>;
  }

  return (
    <div>
      <EntrySelector entries={myEntries} selectedId={selectedEntryId} onSelect={setSelectedEntryId} />

      {currentPick?.status === "flipped" && <FlipBanner pick={currentPick} games={games} />}

      {/* Week nav */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "white", letterSpacing: 1 }}>
          WEEK {week}
          {weekLocked && <span style={{ marginLeft: 10, fontSize: 13, color: "#FF6B35", fontFamily: "Inter, sans-serif", fontWeight: 700 }}>🔒 LOCKED</span>}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button disabled={week <= 1} onClick={() => setWeek(w => w - 1)} className="btn btn-ghost btn-sm">◀</button>
          <select value={week} onChange={(e) => setWeek(Number(e.target.value))}
            style={{ background: "rgba(255,255,255,0.08)", border: "1px solid var(--border)", color: "white", borderRadius: 8, padding: "6px 10px", fontSize: 13 }}>
            {allWeeks.map((w) => <option key={w} value={w}>Week {w}</option>)}
          </select>
          <button disabled={week >= 18} onClick={() => setWeek(w => w + 1)} className="btn btn-ghost btn-sm">▶</button>
        </div>
      </div>

      {/* Pick status */}
      {currentPick && currentPick.status !== "flipped" && (
        <div style={{
          background: "rgba(255,107,53,0.1)", border: "1px solid rgba(255,107,53,0.3)",
          borderRadius: 10, padding: "10px 16px", marginBottom: 16,
          display: "flex", alignItems: "center", gap: 10, fontSize: 14,
        }}>
          <span>🏈</span>
          <span>
            Your pick: <strong style={{ color: "#FF6B35" }}>{currentPick.teamSelected}</strong>
            {currentPick.isAutoAssigned && <span style={{ marginLeft: 8, fontSize: 11, color: "rgba(255,255,255,0.4)" }}>(auto-assigned MNF dog)</span>}
            {currentPick.status === "won" && <span style={{ marginLeft: 8, color: "#00C853", fontWeight: 700 }}>✅ +{currentPick.pointsEarned} pts</span>}
            {currentPick.status === "lost" && <span style={{ marginLeft: 8, color: "#E53935", fontWeight: 700 }}>❌ 0 pts</span>}
            {currentPick.status === "no_pick_available" && <span style={{ marginLeft: 8, color: "rgba(255,255,255,0.4)" }}>No underdog available — 0 pts</span>}
            {!weekLocked && currentPick.status === "pending" && (
              <span style={{ marginLeft: 8, fontSize: 11, color: "rgba(255,255,255,0.35)" }}>— changeable until kickoff</span>
            )}
          </span>
        </div>
      )}

      {!currentPick && !weekLocked && (
        <div style={{
          background: "rgba(229,57,53,0.08)", border: "1px solid rgba(229,57,53,0.25)",
          borderRadius: 10, padding: "10px 16px", marginBottom: 16,
          fontSize: 13, color: "rgba(255,255,255,0.5)",
        }}>
          ⚠️ No pick made yet this week. Pick before the first kickoff!
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "rgba(255,255,255,0.4)" }}>Loading games...</div>
      ) : games.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "rgba(255,255,255,0.4)" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📋</div>
          <p>Games for Week {week} haven't been posted yet.</p>
          <p style={{ fontSize: 12, marginTop: 8, color: "rgba(255,255,255,0.25)" }}>Check back soon — lines are imported as soon as they're available.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
          {games.map((game) => (
            <GameCard
              key={game.id} game={game}
              pick={currentPick?.gameId === game.id ? currentPick : null}
              onPick={makePick} locked={weekLocked || saving}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Leaderboard Tab ──────────────────────────────────────────────────────────

function LeaderboardTab({ contest }) {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [allPicks, setAllPicks] = useState({}); // entryId -> { week -> pick }
  const [kickedOffGames, setKickedOffGames] = useState(new Set()); // gameIds where kickoff has passed
  const [pastWeeks, setPastWeeks] = useState(new Set()); // weeks fully in the past
  const [loading, setLoading] = useState(true);
  const currentWeek = getCurrentNFLWeek() || 1;

  // Default to showing last 6 weeks, scroll back in time
  const weeksAvailable = Array.from({ length: currentWeek }, (_, i) => i + 1);
  const defaultStart = Math.max(1, currentWeek - 5);
  const [visibleStart, setVisibleStart] = useState(defaultStart);
  const visibleWeeks = weeksAvailable.slice(visibleStart - 1, visibleStart + 5);

  // On load (and when the week range changes), scroll the grid fully right so the
  // most recent week — always the last column — is in view on mobile.
  const gridScrollRef = useRef(null);
  useEffect(() => {
    if (loading) return;
    const el = gridScrollRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [loading, visibleStart]);

  useEffect(() => {
    loadAll();
  }, [contest.id]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const now = new Date();

      // Load entries, picks, and ALL games in parallel
      const [entriesSnap, picksSnap, gamesSnap] = await Promise.all([
        getDocs(query(collection(db, "ue_entries"), where("contestId", "==", contest.id))),
        getDocs(query(collection(db, "ue_picks"), where("contestId", "==", contest.id))),
        getDocs(query(collection(db, "ue_games"), where("contestId", "==", contest.id))),
      ]);

      const entriesData = entriesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      entriesData.sort((a, b) => (b.totalPoints || 0) - (a.totalPoints || 0));
      setEntries(entriesData);

      const pickMap = {};
      picksSnap.docs.forEach((d) => {
        const p = { id: d.id, ...d.data() };
        if (!pickMap[p.entryId]) pickMap[p.entryId] = {};
        pickMap[p.entryId][p.week] = p;
      });
      setAllPicks(pickMap);

      // Build set of game IDs whose kickoff has passed — for ALL weeks
      const kicked = new Set();
      // Also track which weeks have ALL games kicked off (fully past weeks)
      const weekGameMap = {}; // week -> [gameIds]
      const weekKickedMap = {}; // week -> [kickedGameIds]
      gamesSnap.docs.forEach((d) => {
        const g = d.data();
        const kickoff = g.kickoff?.toDate ? g.kickoff.toDate() : new Date(g.kickoff || 0);
        const w = g.week;
        if (!weekGameMap[w]) weekGameMap[w] = [];
        if (!weekKickedMap[w]) weekKickedMap[w] = [];
        weekGameMap[w].push(d.id);
        if (kickoff <= now) {
          kicked.add(d.id);
          weekKickedMap[w].push(d.id);
        }
      });
      setKickedOffGames(kicked);

      // A week is "past" (fully visible) only when ALL its games have kicked off
      const past = new Set();
      Object.keys(weekGameMap).forEach((w) => {
        const wNum = parseInt(w);
        if (weekGameMap[w].length > 0 &&
            weekKickedMap[w]?.length === weekGameMap[w].length) {
          past.add(wNum);
        }
      });
      setPastWeeks(past);

    } catch (e) { console.error("Leaderboard load error:", e); }
    setLoading(false);
  };

  const getRankStyle = (i) => {
    if (i === 0) return { medal: "🥇", border: "rgba(255,215,0,0.6)",   bg: "#2a2e0a" };
    if (i === 1) return { medal: "🥈", border: "rgba(192,192,192,0.7)", bg: "#1e2835" };
    if (i === 2) return { medal: "🥉", border: "rgba(205,127,50,0.6)",  bg: "#2a2010" };
    return { medal: null, border: "rgba(255,255,255,0.07)", bg: i % 2 === 0 ? "#0f1e32" : "#0a1628" };
  };

  const canScrollBack = visibleStart > 1;
  const canScrollForward = visibleStart + 5 < currentWeek;

  if (loading) return <p style={{ color: "rgba(255,255,255,0.4)", padding: 20 }}>Loading standings...</p>;
  if (entries.length === 0) return <p style={{ color: "rgba(255,255,255,0.4)", padding: 20 }}>No entries yet.</p>;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "white", letterSpacing: 1 }}>SEASON STANDINGS</div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button onClick={() => setVisibleStart(s => Math.max(1, s - 1))} disabled={!canScrollBack} className="btn btn-ghost btn-sm">◀</button>
          <span style={{ fontSize: 12, color: "rgba(255,255,255,0.3)" }}>Wk {visibleWeeks[0]}–{visibleWeeks[visibleWeeks.length - 1]}</span>
          <button onClick={() => setVisibleStart(s => Math.min(currentWeek - 5, s + 1))} disabled={!canScrollForward} className="btn btn-ghost btn-sm">▶</button>
        </div>
      </div>

      {/* Grid */}
      <div ref={gridScrollRef} style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 540 }}>
          <thead>
            <tr>
              {/* Sticky header left col */}
              <th style={{
                position: "sticky", left: 0, zIndex: 2,
                background: "#0a1628", padding: "8px 8px",
                textAlign: "left", fontSize: 11, fontWeight: 700,
                color: "rgba(255,255,255,0.3)", letterSpacing: 1,
                borderBottom: "1px solid var(--border)",
                minWidth: 170, maxWidth: 170,
              }}>ENTRY</th>
              {visibleWeeks.map((w) => (
                <th key={w} style={{
                  padding: "8px 6px", textAlign: "center",
                  fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.3)",
                  letterSpacing: 1, borderBottom: "1px solid var(--border)",
                  minWidth: 64,
                }}>WK {w}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, i) => {
              const rank = getRankStyle(i);
              const isOwn = entry.ownerUid === user?.uid;
              return (
              <tr key={entry.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                {/* Sticky left col — rank + name + total */}
                <td style={{
                  position: "sticky", left: 0, zIndex: 1,
                  // Must be OPAQUE — this column is pinned and week cells scroll underneath it.
                  // Same green tint as before, layered over a solid navy base.
                  background: isOwn ? "linear-gradient(rgba(0,200,83,0.08), rgba(0,200,83,0.08)), #0a1628" : rank.bg,
                  padding: "8px 6px",
                  borderLeft: isOwn ? "4px solid #00C853" : `3px solid ${rank.border}`,
                  width: 170, minWidth: 170, maxWidth: 170,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {rank.medal
                      ? <span style={{ fontSize: 15, flexShrink: 0 }}>{rank.medal}</span>
                      : <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 15, color: "rgba(255,255,255,0.3)", flexShrink: 0, minWidth: 16, textAlign: "center" }}>{i + 1}</span>
                    }
                    {/* Entry name + username stacked */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                        <span style={{ fontWeight: 700, fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "white" }}>{entry.name}</span>
                        {isOwn && <span style={{ fontSize: 10, flexShrink: 0 }}>🏈</span>}
                      </div>
                      {entry.ownerName && (
                        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.3 }}>
                          {entry.ownerName}
                        </div>
                      )}
                    </div>
                    {/* Total points on the right */}
                    <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, color: isOwn ? "#00C853" : "#FF6B35", letterSpacing: 0.5, lineHeight: 1, flexShrink: 0, textAlign: "right" }}>
                      {entry.totalPoints || 0}
                    </div>
                  </div>
                </td>

                {/* Weekly pick cells */}
                {visibleWeeks.map((w) => {
                  const pick = allPicks[entry.id]?.[w];
                  const isPastWeek = pastWeeks.has(w);
                  const isCurrentWeek = w === currentWeek;

                  // For past weeks — always show (already scored)
                  // For current week — only show if that specific game has kicked off
                  const gameKickedOff = pick?.gameId
                    ? kickedOffGames.has(pick.gameId)
                    : false;
                  const isVisible = isPastWeek || gameKickedOff ||
                    ["won", "lost", "no_pick_available"].includes(pick?.status);

                  // No pick and not visible yet — lock icon
                  if (!pick && isCurrentWeek) {
                    return (
                      <td key={w} style={{ padding: "10px 6px", textAlign: "center", background: isOwn ? "rgba(0,200,83,0.06)" : rank.bg }}>
                        <span style={{ fontSize: 16, opacity: 0.2 }}>🔒</span>
                      </td>
                    );
                  }

                  // No pick in past week
                  if (!pick) {
                    return (
                      <td key={w} style={{ padding: "10px 6px", textAlign: "center", background: isOwn ? "rgba(0,200,83,0.06)" : rank.bg }}>
                        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.15)" }}>—</span>
                      </td>
                    );
                  }

                  // Pick exists but game hasn't kicked off yet — hide it
                  if (!isVisible) {
                    return (
                      <td key={w} style={{ padding: "10px 6px", textAlign: "center", background: isOwn ? "rgba(0,200,83,0.06)" : rank.bg }}>
                        <span style={{ fontSize: 16, opacity: 0.2 }}>🔒</span>
                      </td>
                    );
                  }

                  const isWon = pick.status === "won";
                  const isLost = pick.status === "lost";
                  const isAuto = pick.isAutoAssigned === true;
                  const isPending = pick.status === "pending";
                  const noPickAvail = pick.status === "no_pick_available";

                  const borderColor = isWon ? "#00C853" : isLost ? "#E53935" : isPending ? "rgba(255,107,53,0.4)" : "rgba(255,255,255,0.1)";
                  const team = NFL_TEAMS.find((t) => t.name === pick.teamSelected);

                  if (noPickAvail) {
                    return (
                      <td key={w} style={{ padding: "10px 6px", textAlign: "center", background: rank.bg }}>
                        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.2)", lineHeight: 1.3 }}>no<br/>dog</div>
                      </td>
                    );
                  }

                  return (
                    <td key={w} style={{ padding: "6px", textAlign: "center", background: isOwn ? "rgba(0,200,83,0.06)" : rank.bg }}>
                      <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 2, position: "relative" }}>
                        {/* Team logo with colored border */}
                        <div style={{
                          width: 36, height: 36, borderRadius: "50%",
                          border: `2px solid ${borderColor}`,
                          overflow: "hidden", background: "white",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          position: "relative",
                          boxShadow: isWon ? "0 0 8px rgba(0,200,83,0.4)" : isLost ? "0 0 8px rgba(229,57,53,0.3)" : "none",
                        }}>
                          {team ? (
                            <img
                              src={teamLogoUrl(team.espnId)}
                              alt={pick.teamSelected}
                              width={32} height={32}
                              style={{ objectFit: "contain" }}
                              onError={(e) => { e.target.style.display = "none"; }}
                            />
                          ) : (
                            <span style={{ fontSize: 9, color: "#333", fontWeight: 700 }}>
                              {pick.teamSelected?.split(" ").pop()?.substring(0, 3)}
                            </span>
                          )}
                        </div>

                        {/* Auto badge */}
                        {isAuto && (
                          <div style={{
                            position: "absolute", top: -4, right: -4,
                            fontSize: 9, background: "#333", borderRadius: "50%",
                            width: 14, height: 14, display: "flex", alignItems: "center", justifyContent: "center",
                          }}>🤖</div>
                        )}

                        {/* Result / points */}
                        <div style={{ fontSize: 10, fontWeight: 700, lineHeight: 1,
                          color: isWon ? "#00C853" : isLost ? "#E53935" : "rgba(255,255,255,0.35)"
                        }}>
                          {isWon ? `+${pick.pointsEarned}` : isLost ? "0" : isPending ? "⏳" : ""}
                        </div>
                      </div>
                    </td>
                  );
                })}
              </tr>
            );})}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: "rgba(255,255,255,0.2)", display: "flex", gap: 16, flexWrap: "wrap" }}>
        <span>🟢 Won · 🔴 Lost · ⏳ Pending · 🤖 Auto-assigned · 🔒 Not yet kicked off</span>
      </div>
    </div>
  );
}

// ─── Entry Pick History Card ──────────────────────────────────────────────────

function EntryHistoryCard({ entry, contest, currentWeek, onMakePick, onEntryRenamed }) {
  const [expanded, setExpanded] = useState(false);
  const [picks, setPicks] = useState([]);
  const [loadingPicks, setLoadingPicks] = useState(false);
  const [weekPick, setWeekPick] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(entry.name);
  const [savingName, setSavingName] = useState(false);

  // Can rename until first game of current week kicks off
  const canRename = !weekPick || weekPick.status === "pending" || weekPick.status === "flipped";

  useEffect(() => {
    getDocs(query(
      collection(db, "ue_picks"),
      where("entryId", "==", entry.id),
      where("week", "==", currentWeek)
    )).then((snap) => {
      if (!snap.empty) setWeekPick({ id: snap.docs[0].id, ...snap.docs[0].data() });
    });
  }, [entry.id, currentWeek]);

  const saveName = async () => {
    if (!nameDraft.trim() || nameDraft.trim() === entry.name) { setEditingName(false); return; }
    setSavingName(true);
    try {
      await updateDoc(doc(db, "ue_entries", entry.id), { name: nameDraft.trim() });
      onEntryRenamed?.();
    } catch (e) { console.error("Failed to rename entry:", e); }
    setSavingName(false);
    setEditingName(false);
  };

  const loadHistory = async () => {
    if (picks.length > 0) { setExpanded(!expanded); return; }
    setLoadingPicks(true);
    const snap = await getDocs(query(
      collection(db, "ue_picks"),
      where("entryId", "==", entry.id)
    ));
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    data.sort((a, b) => (a.week || 0) - (b.week || 0));
    setPicks(data);
    setLoadingPicks(false);
    setExpanded(true);
  };

  const statusColor = (s) => ({ won: "#00C853", lost: "#E53935", pending: "#FF6B35", auto: "rgba(255,255,255,0.4)", flipped: "#E53935", no_pick_available: "rgba(255,255,255,0.3)" }[s] || "rgba(255,255,255,0.4)");
  const statusLabel = (s) => ({ won: "✅ Won", lost: "❌ Lost", pending: "⏳ Pending", auto: "🤖 Auto", flipped: "⚠️ Flipped", no_pick_available: "— No dog" }[s] || s);

  const hasPickThisWeek = weekPick && weekPick.status !== "flipped";
  const needsPick = !weekPick || weekPick.status === "flipped";

  return (
    <div className="card" style={{ padding: 0, overflow: "hidden", borderColor: needsPick ? "rgba(255,107,53,0.3)" : "var(--border)" }}>
      {/* Entry header */}
      <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {editingName ? (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") { setEditingName(false); setNameDraft(entry.name); } }}
                className="input"
                maxLength={30}
                autoFocus
                style={{ fontSize: 14, padding: "4px 8px", flex: 1 }}
              />
              <button onClick={saveName} disabled={savingName} className="btn btn-sm" style={{ background: "#FF6B35", color: "white", fontWeight: 700, flexShrink: 0 }}>
                {savingName ? "..." : "Save"}
              </button>
              <button onClick={() => { setEditingName(false); setNameDraft(entry.name); }} className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }}>✕</button>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ fontWeight: 700, fontSize: 15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{entry.name}</div>
              {canRename && (
                <button onClick={() => setEditingName(true)} style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.3)", fontSize: 13, padding: "0 2px", flexShrink: 0 }} title="Rename entry">
                  ✏️
                </button>
              )}
            </div>
          )}
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 2 }}>
            {entry.totalPoints || 0} pts season total
          </div>
        </div>

        {/* Current week pick status */}
        <div style={{ textAlign: "right" }}>
          {hasPickThisWeek ? (
            <div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginBottom: 2 }}>WK {currentWeek} PICK</div>
              <div style={{ fontWeight: 700, fontSize: 13, color: statusColor(weekPick.status) }}>
                {weekPick.teamSelected || "—"}
              </div>
              {weekPick.spread && (
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>+{weekPick.spread}</div>
              )}
            </div>
          ) : (
            <button
              onClick={() => onMakePick(entry.id)}
              className="btn btn-sm"
              style={{ background: "#FF6B35", color: "white", fontWeight: 700 }}
            >
              {weekPick?.status === "flipped" ? "⚠️ Repick" : "🏈 Pick Now"}
            </button>
          )}
        </div>
      </div>

      {/* Expand history */}
      <div
        onClick={loadHistory}
        style={{
          padding: "8px 16px", borderTop: "1px solid var(--border)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          cursor: "pointer", background: "rgba(255,255,255,0.02)",
        }}
      >
        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.35)" }}>
          {loadingPicks ? "Loading..." : expanded ? "Hide pick history" : "View pick history"}
        </span>
        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.25)" }}>{expanded ? "▲" : "▼"}</span>
      </div>

      {/* History rows */}
      {expanded && picks.length > 0 && (
        <div style={{ borderTop: "1px solid var(--border)" }}>
          {picks.map((pick) => (
            <div key={pick.id} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "8px 16px", borderBottom: "1px solid rgba(255,255,255,0.04)",
              fontSize: 13,
            }}>
              <div style={{ color: "rgba(255,255,255,0.4)", minWidth: 60 }}>Week {pick.week}</div>
              <div style={{ flex: 1, fontWeight: pick.status === "won" ? 700 : 400, color: "white" }}>
                {pick.teamSelected || "—"}
                {pick.isAutoAssigned && <span style={{ marginLeft: 6, fontSize: 10, color: "rgba(255,255,255,0.3)" }}>auto</span>}
              </div>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                {pick.spread && (
                  <span style={{ fontSize: 12, color: "rgba(255,255,255,0.3)" }}>+{pick.closingSpread || pick.spread}</span>
                )}
                <span style={{ fontWeight: 700, color: statusColor(pick.status) }}>
                  {pick.status === "won" ? `+${pick.pointsEarned}` : statusLabel(pick.status)}
                </span>
              </div>
            </div>
          ))}
          {picks.length === 0 && (
            <div style={{ padding: "12px 16px", fontSize: 13, color: "rgba(255,255,255,0.3)" }}>No picks yet.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Entries Tab ──────────────────────────────────────────────────────────────

function EntriesTab({ contest, myEntries, onEntriesChanged, onMakePick, focusInput }) {
  const { user } = useAuth();
  const [newEntryName, setNewEntryName] = useState("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [reminders, setReminders] = useState(true);
  const currentWeek = getCurrentNFLWeek() || 1;
  const entryInputRef = useRef(null);

  // When focusInput changes to true, scroll input into view and focus it
  useEffect(() => {
    if (!focusInput) return;
    const timer = setTimeout(() => {
      if (entryInputRef.current) {
        entryInputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        entryInputRef.current.focus();
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [focusInput]);

  // Load reminder preference on mount
  useEffect(() => {
    if (!user?.uid) return;
    getDoc(doc(db, "users", user.uid)).then((snap) => {
      if (snap.exists() && snap.data().uePickReminders === false) {
        setReminders(false);
      }
    });
  }, [user?.uid]);

  const toggleReminders = async (val) => {
    setReminders(val);
    try {
      await updateDoc(doc(db, "users", user.uid), { uePickReminders: val });
    } catch (e) { console.error("Failed to save reminder preference:", e); }
  };

  const canAdd = myEntries.length < (contest.maxEntriesPerUser || 1);

  const addEntry = async () => {
    setError("");
    if (!newEntryName.trim()) { setError("Enter a name for your entry."); return; }
    if (newEntryName.trim().length > 30) { setError("Name must be 30 characters or less."); return; }
    if (adding) return; // prevent double-submit
    setAdding(true);
    try {
      // Re-check entry count server-side before writing to prevent race condition
      const currentSnap = await getDocs(query(
        collection(db, "ue_entries"),
        where("contestId", "==", contest.id),
        where("ownerUid", "==", user.uid)
      ));
      const max = contest.maxEntriesPerUser || 1;
      if (currentSnap.size >= max) {
        setError(`You've already reached the maximum of ${max} ${max === 1 ? "entry" : "entries"}.`);
        setAdding(false);
        return;
      }
      await addDoc(collection(db, "ue_entries"), {
        contestId: contest.id, ownerUid: user.uid, ownerName: user.displayName,
        name: newEntryName.trim(), totalPoints: 0, createdAt: serverTimestamp(),
      });
      setNewEntryName("");
      onEntriesChanged();
    } catch (e) { setError("Error adding entry. Try again."); }
    setAdding(false);
  };

  // Shareable invite link
  const inviteLink = `${window.location.origin}/underdog-edge?join=${contest.joinCode}`;
  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div>
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "white", letterSpacing: 1, marginBottom: 16 }}>
        MY ENTRIES
      </div>

      {/* Entry cards with pick history */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
        {myEntries.length === 0 && (
          <p style={{ color: "rgba(255,255,255,0.4)", marginBottom: 8 }}>You haven't added any entries yet.</p>
        )}
        {myEntries.map((e) => (
          <EntryHistoryCard
            key={e.id} entry={e} contest={contest}
            currentWeek={currentWeek}
            onMakePick={onMakePick}
            onEntryRenamed={onEntriesChanged}
          />
        ))}
      </div>

      {/* Add entry */}
      {canAdd && (
        <div className="card" style={{ padding: 20, marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "white", marginBottom: 10 }}>
            Add Entry ({myEntries.length}/{contest.maxEntriesPerUser || 1})
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <input ref={entryInputRef} value={newEntryName} onChange={(e) => setNewEntryName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addEntry()}
              placeholder="Entry name" className="input" maxLength={30} />
            <button onClick={addEntry} disabled={adding} className="btn btn-sm"
              style={{ background: "#FF6B35", color: "white", fontWeight: 700, flexShrink: 0 }}>
              {adding ? "..." : "+ Add"}
            </button>
          </div>
          {error && <p className="error-text" style={{ marginTop: 8 }}>{error}</p>}
        </div>
      )}

      {!canAdd && myEntries.length > 0 && (
        <p style={{ fontSize: 13, color: "rgba(255,255,255,0.3)", marginBottom: 16 }}>
          You've reached the maximum of {contest.maxEntriesPerUser} {contest.maxEntriesPerUser === 1 ? "entry" : "entries"} for this pool.
        </p>
      )}

      {/* Invite section */}
      {contest.openInvite && (
        <div className="card" style={{ padding: 18, borderColor: "rgba(255,107,53,0.2)" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.3)", letterSpacing: 1, marginBottom: 12 }}>INVITE PLAYERS</div>

          {/* Join code */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginBottom: 4 }}>JOIN CODE</div>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: "#FF6B35", letterSpacing: 6 }}>{contest.joinCode}</div>
            </div>
          </div>

          {/* Invite link */}
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginBottom: 6 }}>INVITE LINK</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{
              flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid var(--border)",
              borderRadius: 8, padding: "8px 12px", fontSize: 12,
              color: "rgba(255,255,255,0.4)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {inviteLink}
            </div>
            <button onClick={copyInviteLink} className="btn btn-sm"
              style={{ background: copied ? "#00C853" : "rgba(255,255,255,0.1)", color: "white", fontWeight: 700, flexShrink: 0, transition: "background 0.2s" }}>
              {copied ? "✓ Copied!" : "Copy"}
            </button>
          </div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.25)", marginTop: 8 }}>
            Anyone with this link will be taken directly to the join screen.
          </div>
        </div>
      )}

      {/* Email reminder toggle */}
      <div style={{
        marginTop: 16, padding: "14px 16px",
        background: "rgba(255,255,255,0.03)", borderRadius: 10,
        border: "1px solid rgba(255,255,255,0.07)",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "white", marginBottom: 2 }}>Pick Reminders</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", lineHeight: 1.4 }}>
            Email reminders Thursday morning and Saturday evening when you haven't picked yet.
          </div>
        </div>
        <button
          onClick={() => toggleReminders(!reminders)}
          style={{
            width: 44, height: 24, borderRadius: 12, border: "none",
            background: reminders ? "#FF6B35" : "rgba(255,255,255,0.15)",
            position: "relative", cursor: "pointer", flexShrink: 0, marginLeft: 16,
            transition: "background 0.2s",
          }}
        >
          <div style={{
            width: 18, height: 18, borderRadius: "50%", background: "white",
            position: "absolute", top: 3,
            left: reminders ? 23 : 3,
            transition: "left 0.2s",
            boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
          }} />
        </button>
      </div>
    </div>
  );
}

// ─── Main Contest Detail ──────────────────────────────────────────────────────

export default function UEContestDetail({ onMount, onLeave }) {
  const { contestId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contest, setContest] = useState(null);
  const [myEntries, setMyEntries] = useState([]);
  const [tab, setTab] = useState("picks");
  const [loading, setLoading] = useState(true);
  const [jumpToEntryId, setJumpToEntryId] = useState(null);
  const [focusEntryInput, setFocusEntryInput] = useState(false);
  const [exporting, setExporting] = useState(false);

  const exportPool = async () => {
    setExporting(true);
    try {
      const fn = getFunctions();
      const result = await httpsCallable(fn, "getUEContestEmails")({ contestId });
      const { entries, playedWeeks } = result.data;
      const weekHeaders = playedWeeks.flatMap((w) => [`Wk${w} Pick`, `Wk${w} Result`, `Wk${w} Pts`]);
      const header = ["Rank", "Entry Name", "Display Name", "Email", "Total Points", ...weekHeaders].join(",");
      const lines = entries.map((r) => {
        const weekCols = playedWeeks.flatMap((w) => [
          `"${r[`week${w}_pick`] || "—"}"`,
          `"${r[`week${w}_result`] || "—"}"`,
          r[`week${w}_pts`] ?? 0,
        ]);
        return [r.rank, `"${r.entryName}"`, `"${r.ownerName}"`, `"${r.email}"`, r.totalPoints, ...weekCols].join(",");
      });
      const csv = [header, ...lines].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${contest?.name || "underdog-edge"}-export.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error("Export failed:", e); }
    setExporting(false);
  };
  const [copied, setCopied] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteStatus, setInviteStatus] = useState("");
  const [contestMessage, setContestMessage] = useState("");
  const [editingMessage, setEditingMessage] = useState(false);
  const [messageDraft, setMessageDraft] = useState("");
  const [savingMessage, setSavingMessage] = useState(false);

  useEffect(() => { loadContest(); }, [contestId]);

  const loadContest = async () => {
    setLoading(true);
    try {
      // Fire contest + entries reads in parallel
      const [contestSnap, entriesSnap] = await Promise.all([
        getDoc(doc(db, "ue_contests", contestId)),
        getDocs(query(
          collection(db, "ue_entries"),
          where("contestId", "==", contestId),
          where("ownerUid", "==", user.uid)
        )),
      ]);

      if (!contestSnap.exists()) { navigate("/underdog-edge"); return; }
      const contestData = { id: contestSnap.id, ...contestSnap.data() };
      setContest(contestData);
      setContestMessage(contestData.message || "");
      setMessageDraft(contestData.message || "");
      onMount?.(contestData.name, () => navigate("/underdog-edge"));

      const entries = entriesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setMyEntries(entries);
      if (entries.length === 0) setTab("entries");
    } catch (e) { console.error("Error loading contest:", e); }
    setLoading(false);
  };

  const loadMyEntries = async (c) => {
    const snap = await getDocs(query(
      collection(db, "ue_entries"),
      where("contestId", "==", c.id),
      where("ownerUid", "==", user.uid)
    ));
    const entries = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setMyEntries(entries);
    if (entries.length === 0) setTab("entries");
  };

  // Called from entries tab "Pick Now" button — jump to picks tab for this entry
  const handleMakePick = (entryId) => {
    setJumpToEntryId(entryId);
    setTab("picks");
  };

  const handleLeave = () => { onLeave?.(); navigate("/underdog-edge"); };

  const isOwner = user?.uid === contest?.adminUid;
  const isOpenInvite = contest?.openInvite !== false;
  const canInvite = isOwner || isOpenInvite;
  const maxEntries = contest?.maxEntriesPerUser || 1;
  const canAddEntry = myEntries.length < maxEntries;
  const inviteLink = contest ? `${window.location.origin}/underdog-edge?join=${contest.joinCode}` : "";

  const copyJoinCode = () => {
    navigator.clipboard.writeText(contest.joinCode).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  const sendInviteEmail = async () => {
    setInviteStatus("");
    if (!inviteEmail.trim()) { setInviteStatus("Please enter an email address."); return; }
    setInviteSending(true);
    try {
      const fn = getFunctions();
      await httpsCallable(fn, "sendInviteEmail")({
        toEmail: inviteEmail.trim(),
        contestName: contest.name,
        joinCode: contest.joinCode,
        fromName: user.displayName || "A friend",
        gameType: "underdog-edge",
      });
      setInviteStatus("Invite sent!");
      setInviteEmail("");
      setTimeout(() => { setInviteStatus(""); setShowInvite(false); }, 2500);
    } catch (err) { setInviteStatus("Failed to send. Try again."); }
    setInviteSending(false);
  };

  const saveMessage = async () => {
    setSavingMessage(true);
    try {
      await updateDoc(doc(db, "ue_contests", contest.id), { message: messageDraft.trim() });
      setContestMessage(messageDraft.trim());
      setEditingMessage(false);
    } catch (e) { console.error("Failed to save message:", e); }
    setSavingMessage(false);
  };

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "rgba(255,255,255,0.4)" }}>Loading pool...</div>;
  if (!contest) return null;

  return (
    <div className="animate-fade-in">
      {/* Back button */}
      <button onClick={handleLeave} className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }}>← Back to Pools</button>

      {/* Contest header — WC Pool style */}
      <div style={{
        background: "linear-gradient(135deg, #112240, #1a2d4a)",
        border: "1px solid rgba(255,107,53,0.2)",
        borderRadius: 16, padding: "16px 20px", marginBottom: 12,
      }}>
        {/* Top row: name + badges + action buttons */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10, marginBottom: 10 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 26, letterSpacing: 1, color: "white" }}>
                {contest.name}
              </div>
              {isOwner && <span className="badge badge-orange">ADMIN</span>}
              <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>· {contest.season} Season</span>
              {getCurrentNFLWeek() && (
                <span style={{
                  fontSize: 11, fontWeight: 700, letterSpacing: 1,
                  color: "#FF6B35",
                  background: "rgba(255,107,53,0.12)",
                  border: "1px solid rgba(255,107,53,0.3)",
                  borderRadius: 100, padding: "2px 10px",
                }}>
                  WEEK {getCurrentNFLWeek()}
                </span>
              )}
            </div>
            {canInvite && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>Join code:</span>
                <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, color: "#FF6B35", letterSpacing: 4 }}>{contest.joinCode}</span>
                <button onClick={copyJoinCode} className="btn btn-ghost btn-sm" style={{ padding: "3px 10px" }}>
                  {copied ? "✓ Copied!" : "Copy"}
                </button>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button
              onClick={() => { setJumpToEntryId(null); setFocusEntryInput(f => !f); setTab("entries"); }}
              disabled={!canAddEntry}
              className="btn btn-success btn-sm"
              style={{ opacity: canAddEntry ? 1 : 0.4, cursor: canAddEntry ? "pointer" : "not-allowed" }}
            >
              + Add Entry
            </button>
            {canInvite && (
              <button onClick={() => setShowInvite(!showInvite)} className="btn btn-outline btn-sm" style={{ borderColor: "#FF6B35", color: "#FF6B35" }}>
                📧 Invite
              </button>
            )}
            {isOwner && (
              <button onClick={exportPool} disabled={exporting} className="btn btn-ghost btn-sm">
                {exporting ? "..." : "⬇ Export"}
              </button>
            )}
          </div>
        </div>

        {/* Invite panel */}
        {showInvite && canInvite && (
          <div style={{ paddingTop: 14, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginBottom: 12 }}>
              Invite someone to <strong style={{ color: "white" }}>{contest.name}</strong>
            </div>

            {/* Share link */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: 0.5, marginBottom: 6 }}>SHARE LINK</div>
              <div style={{ display: "flex", gap: 8 }}>
                <input readOnly value={inviteLink} className="input"
                  style={{ flex: 1, fontSize: 12, color: "rgba(255,255,255,0.6)" }}
                  onFocus={(e) => e.target.select()} />
                <button onClick={() => { navigator.clipboard.writeText(inviteLink); setInviteStatus("Link copied!"); setTimeout(() => setInviteStatus(""), 2000); }}
                  className="btn btn-ghost btn-sm">📋 Copy</button>
              </div>
            </div>

            {/* Email invite */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: 0.5, marginBottom: 6 }}>SEND EMAIL INVITE</div>
              <div style={{ display: "flex", gap: 8 }}>
                <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendInviteEmail()}
                  placeholder="Email address" className="input" style={{ flex: 1 }} />
                <button onClick={sendInviteEmail} disabled={inviteSending} className="btn btn-sm" style={{ background: "#FF6B35", color: "white", fontWeight: 700, flexShrink: 0 }}>
                  {inviteSending ? "..." : "Send"}
                </button>
              </div>
            </div>

            {inviteStatus && (
              <p className={inviteStatus.includes("sent") || inviteStatus.includes("copied") ? "success-text" : "error-text"} style={{ marginTop: 8 }}>
                {inviteStatus}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Organizer message */}
      {(contestMessage || isOwner) && (
        <div style={{
          background: "#1a2d4a", border: "1px solid rgba(255,107,53,0.2)",
          borderRadius: 12, padding: "14px 18px", marginBottom: 16,
        }}>
          {!editingMessage ? (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,107,53,0.6)", letterSpacing: 1, marginBottom: 6 }}>📌 FROM THE ORGANIZER</div>
                {contestMessage ? (
                  <p style={{ fontSize: 14, color: "rgba(255,255,255,0.8)", lineHeight: 1.6, margin: 0, whiteSpace: "pre-wrap" }}>{contestMessage}</p>
                ) : (
                  <p style={{ fontSize: 13, color: "rgba(255,255,255,0.3)", margin: 0, fontStyle: "italic" }}>No message yet. Click Edit to add one.</p>
                )}
              </div>
              {isOwner && (
                <button onClick={() => setEditingMessage(true)} className="btn btn-ghost btn-sm">Edit</button>
              )}
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,107,53,0.6)", letterSpacing: 1, marginBottom: 8 }}>📌 FROM THE ORGANIZER</div>
              <textarea value={messageDraft} onChange={(e) => setMessageDraft(e.target.value)}
                className="input" maxLength={500}
                style={{ minHeight: 80, resize: "vertical", lineHeight: 1.5, marginBottom: 10 }} />
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={saveMessage} disabled={savingMessage} className="btn btn-sm" style={{ background: "#FF6B35", color: "white", fontWeight: 700 }}>
                  {savingMessage ? "Saving..." : "Save"}
                </button>
                <button onClick={() => { setEditingMessage(false); setMessageDraft(contestMessage); }} className="btn btn-ghost btn-sm">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="nav-tabs" style={{ marginBottom: 24 }}>
        {[
          { id: "picks", label: "🏈 Make Picks" },
          { id: "leaderboard", label: "🏆 Standings" },
          { id: "entries", label: "📋 My Entries" },
        ].map((t) => (
          <button key={t.id}
            className={"nav-tab" + (tab === t.id ? " active" : "")}
            onClick={() => setTab(t.id)}
            style={tab === t.id ? { background: "#FF6B35", color: "white" } : {}}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "picks" && (
        <WeekPicksTab
          contest={contest} entries={myEntries} myEntries={myEntries}
          initialEntryId={jumpToEntryId}
        />
      )}
      {tab === "leaderboard" && <LeaderboardTab contest={contest} />}
      {tab === "entries" && (
        <EntriesTab
          contest={contest} myEntries={myEntries}
          onEntriesChanged={() => loadMyEntries(contest)}
          onMakePick={handleMakePick}
          focusInput={focusEntryInput}
        />
      )}
    </div>
  );
}
